import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Briefcase, Mail, Pencil, Ban, RotateCcw, Trash2 } from 'lucide-react';
import {
  PageHeader, DataTable, Person, StatusPill, Button, Modal, SlideOver,
} from '../components';
import { adminAgencyService } from '../services/agencyService';
import {
  inputClass, FieldLabel, errorMessage, Field, CONNECTION_STATUS, stampOf,
} from './agency/shared';

/**
 * Admin > Agencies — the platform admin creates every staffing agency.
 *
 * Add agency makes the agency AND its administrator in one go: the agency
 * gets a permanent id (AOCH + 5 digits) which is also the administrator's
 * login, and the administrator is emailed a link to set their 6-digit PIN.
 * Care homes then find the agency by name and ask to connect; the agency
 * accepts or declines from its own workspace. Nothing here connects an agency
 * to a home.
 */

const EMPTY = { name: '', email: '', phone: '', address: '', admin_name: '', admin_email: '' };

const AgencyForm = ({ open, agency, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const editing = Boolean(agency);

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(agency ? {
      name: agency.name || '',
      email: agency.email || '',
      phone: agency.phone || '',
      address: agency.address || '',
      admin_name: agency.admin?.name || '',
      admin_email: agency.admin?.email || '',
    } : EMPTY);
  }, [open, agency]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) { setError('Enter the agency name.'); return; }
    if (!form.admin_name.trim() || !form.admin_email.trim()) { setError("Enter the administrator's name and email."); return; }
    setBusy(true);
    setError('');
    try {
      const res = editing
        ? await adminAgencyService.update(agency.id, form)
        : await adminAgencyService.create(form);
      toast.success(res.message || 'Saved');
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={busy ? undefined : onClose}
      title={editing ? `Edit ${agency.name}` : 'Add agency'}
      maxWidth="max-w-lg"
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy}>{editing ? 'Save changes' : 'Create and send invitation'}</Button>
        </div>
      )}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <FieldLabel required>Agency name</FieldLabel>
          <input className={inputClass} value={form.name} onChange={set('name')} placeholder="e.g. Meridian Care Staffing" />
        </div>
        <div>
          <FieldLabel>Agency email</FieldLabel>
          <input className={inputClass} type="email" value={form.email} onChange={set('email')} placeholder="bookings@agency.co.uk" />
        </div>
        <div>
          <FieldLabel>Phone</FieldLabel>
          <input className={inputClass} value={form.phone} onChange={set('phone')} />
        </div>
        <div className="sm:col-span-2">
          <FieldLabel>Address</FieldLabel>
          <input className={inputClass} value={form.address} onChange={set('address')} />
        </div>
        <div className="sm:col-span-2 pt-2 border-t border-[#EEF2F6] dark:border-gray-800">
          <p className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100">Agency administrator</p>
          <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
            {editing
              ? 'Their login ID never changes. A new email is told to the old address.'
              : 'They sign in with the agency ID and a 6-digit PIN they set from the emailed link.'}
          </p>
        </div>
        <div>
          <FieldLabel required>Full name</FieldLabel>
          <input className={inputClass} value={form.admin_name} onChange={set('admin_name')} />
        </div>
        <div>
          <FieldLabel required>Email</FieldLabel>
          <input className={inputClass} type="email" value={form.admin_email} onChange={set('admin_email')} />
        </div>
      </div>
      {error && <p className="mt-3 text-[12.5px] text-rose-600">{error}</p>}
    </Modal>
  );
};

const DeleteAgency = ({ agency, onClose, onDeleted }) => {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => setTyped(''), [agency]);
  if (!agency) return null;
  const remove = async () => {
    setBusy(true);
    try {
      const res = await adminAgencyService.remove(agency.id);
      toast.success(res.message || 'Deleted');
      onDeleted();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      isOpen
      onClose={busy ? undefined : onClose}
      title="Delete agency"
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" onClick={remove} loading={busy} disabled={typed.trim() !== agency.name}>Delete for good</Button>
        </div>
      )}
    >
      <p className="text-[13.5px] text-[#475569] dark:text-gray-300">
        This deletes <strong>{agency.name}</strong>, its administrator, every one of its staff logins, its care-home
        connections and all of its bookings and clocked hours. It cannot be undone. To keep the history, suspend the
        agency instead.
      </p>
      <FieldLabel>Type the agency name to confirm</FieldLabel>
      <input className={inputClass} value={typed} onChange={(e) => setTyped(e.target.value)} />
    </Modal>
  );
};

const Agencies = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(null); // { agency } — agency null = new
  const [detail, setDetail] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAgencyService.list();
      setRows(res.data || []);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not load agencies.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setDetail({ ...row, connections: null });
    try {
      const res = await adminAgencyService.get(row.id);
      setDetail(res.data);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const resend = async (row) => {
    try {
      const res = await adminAgencyService.resendInvite(row.id);
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const toggleActive = async (row) => {
    try {
      const res = await adminAgencyService.setActive(row.id, !row.is_active);
      toast.success(res.message);
      load();
      setDetail(null);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.name, r.org_id, r.email, r.admin?.name, r.admin?.email]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  const columns = [
    {
      key: 'name', header: 'Agency', sortable: true,
      render: (r) => <Person name={r.name} sub={r.org_id} />,
    },
    {
      key: 'admin', header: 'Administrator',
      render: (r) => (
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-100 truncate">{r.admin?.name || '—'}</p>
          <p className="text-[12px] text-[#94A3B8] truncate">{r.admin?.email || ''}</p>
        </div>
      ),
    },
    {
      key: 'setup', header: 'Setup',
      render: (r) => (r.admin?.setup_complete
        ? <StatusPill tone="good">PIN set</StatusPill>
        : <StatusPill tone="warn">Invitation sent</StatusPill>),
    },
    { key: 'staff_count', header: 'Staff', sortable: true, render: (r) => r.staff_count },
    {
      key: 'connected_homes', header: 'Care homes', sortable: true,
      render: (r) => (
        <span className="text-[13px]">
          {r.connected_homes}
          {r.pending_requests > 0 && <span className="ml-1.5 text-amber-600">+{r.pending_requests} pending</span>}
        </span>
      ),
    },
    {
      key: 'is_active', header: 'Status',
      render: (r) => (r.is_active ? <StatusPill tone="good">Active</StatusPill> : <StatusPill tone="crit">Suspended</StatusPill>),
    },
    {
      key: 'actions', header: '', align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" icon={<Pencil className="w-4 h-4" />} aria-label="Edit" onClick={() => setForm({ agency: r })} />
          {!r.admin?.setup_complete && (
            <Button size="sm" variant="ghost" icon={<Mail className="w-4 h-4" />} aria-label="Resend invitation" onClick={() => resend(r)} />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Agencies"
        count={rows.length}
        subtitle="Staffing agencies that supply carers to care homes. Care homes find them by name and send a connection request."
        actions={<Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setForm({ agency: null })}>Add agency</Button>}
      />

      <DataTable
        title="All agencies"
        count={filtered.length}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, AOCH ID or email…"
        columns={columns}
        data={filtered}
        loading={loading}
        rowKey={(r) => r.id}
        onRowClick={openDetail}
        emptyIcon={Briefcase}
        emptyTitle="No agencies yet"
        emptyText="Add an agency to invite its administrator."
      />

      <AgencyForm
        open={Boolean(form)}
        agency={form?.agency || null}
        onClose={() => setForm(null)}
        onSaved={() => { setForm(null); load(); }}
      />

      <SlideOver
        isOpen={Boolean(detail)}
        onClose={() => setDetail(null)}
        title={detail?.name}
        subtitle={detail?.org_id}
        footer={detail && (
          <div className="flex flex-wrap justify-between gap-2">
            <Button variant="danger-soft" leftIcon={<Trash2 className="w-4 h-4" />} onClick={() => { setDeleting(detail); setDetail(null); }}>Delete</Button>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                leftIcon={detail.is_active ? <Ban className="w-4 h-4" /> : <RotateCcw className="w-4 h-4" />}
                onClick={() => toggleActive(detail)}
              >
                {detail.is_active ? 'Suspend' : 'Reactivate'}
              </Button>
              <Button leftIcon={<Pencil className="w-4 h-4" />} onClick={() => { setForm({ agency: detail }); setDetail(null); }}>Edit</Button>
            </div>
          </div>
        )}
      >
        {detail && (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Agency ID / admin login">{detail.org_id}</Field>
              <Field label="Status">{detail.is_active ? 'Active' : 'Suspended'}</Field>
              <Field label="Email">{detail.email}</Field>
              <Field label="Phone">{detail.phone}</Field>
              <div className="col-span-2"><Field label="Address">{detail.address}</Field></div>
              <Field label="Administrator">{detail.admin?.name}</Field>
              <Field label="Administrator email">{detail.admin?.email}</Field>
              <Field label="Setup">
                {detail.admin?.setup_complete
                  ? 'PIN set'
                  : `Invitation sent${detail.admin?.invite_expires_at ? ` · link expires ${stampOf(detail.admin.invite_expires_at)}` : ''}`}
              </Field>
              <Field label="Active staff">{detail.staff_count}</Field>
            </div>
            <div>
              <p className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100 mb-2">Care homes</p>
              {detail.connections === null && <p className="text-[13px] text-[#94A3B8]">Loading…</p>}
              {detail.connections?.length === 0 && <p className="text-[13px] text-[#94A3B8]">No care home has asked to connect yet.</p>}
              <ul className="flex flex-col divide-y divide-[#EEF2F6] dark:divide-gray-800">
                {(detail.connections || []).map((c) => (
                  <li key={c.id} className="py-2.5 flex items-center justify-between gap-3">
                    <span className="text-[13.5px] text-[#0B1220] dark:text-gray-100 truncate">{c.home_name}</span>
                    <StatusPill tone={CONNECTION_STATUS[c.status]?.tone}>{CONNECTION_STATUS[c.status]?.label || c.status}</StatusPill>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </SlideOver>

      <DeleteAgency agency={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }} />
    </div>
  );
};

export default Agencies;
